import { useCallback } from 'react'

import { apiUrl, toQuery, useApiRequest } from '../useApiRequest'

import { PassProduct } from '../../types/booking/booking'

/**
 * Prepaid pass products. Edits create a new immutable version; passes already bought keep their terms.
 */
export function usePassProducts() {
  const { run, loading, error, resetState } = useApiRequest()

  const list = useCallback(
    (includeInactive = false) =>
      run(({ fetcher }) =>
        fetcher<PassProduct[]>(apiUrl(`/pass-products${toQuery({ includeInactive })}`))
      ),
    [run]
  )

  /** Every version of a pass product, newest first. */
  const versions = useCallback(
    (id: number) => run(({ fetcher }) => fetcher<PassProduct[]>(apiUrl(`/pass-products/${id}/versions`))),
    [run]
  )

  const create = useCallback(
    (product: PassProduct) =>
      run(({ poster }) => poster<PassProduct>(apiUrl('/pass-products'), product, 'application/json')),
    [run]
  )

  const update = useCallback(
    (id: number, product: PassProduct) =>
      run(({ putter }) =>
        putter<PassProduct>(apiUrl(`/pass-products/${id}`), product, 'application/json')
      ),
    [run]
  )

  const updateStatus = useCallback(
    (id: number, status: { active?: boolean; suspended?: boolean }) =>
      run(({ patcher }) =>
        patcher<PassProduct>(apiUrl(`/pass-products/${id}/status`), status, 'application/json')
      ),
    [run]
  )

  return { list, versions, create, update, updateStatus, loading, error, resetState }
}
